import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  LinearProgress,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from 'react-query'
import SpreadsheetService from '../../../APIs/Python/APIs/SpreadsheetService'
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import * as React from 'react'
import { useState } from 'react'
import useInterval from '../../../Functions/SourcedOnline/useInterval'
import EntryStepsHeader from './EntryStepsHeader'
import { AxiosResponse } from 'axios'
import {
  Delete,
  OpenInNew,
  Save,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { useUserContext } from '../../../Contexts/UserConfig'
import FullScreenModal from './FullScreenModal'
import { useMessagePopupContext } from '../../../garvan-react/Components/ModalFeedback/MessagePopup'

export interface SpreadsheetStatus {
  Status: string
  StatusMessage: string
  URL: string
}

function DataEntry() {
  const { study_id } = useParams()
  const { whoAmI } = useUserContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { setError } = useMessagePopupContext()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const [openFull, setOpenFull] = useState(false)

  const {
    data: spreadsheet,
    refetch,
    error,
  } = useQuery<SpreadsheetStatus, AxiosResponse>(
    ['SpreadsheetService', 'checkSpreadsheet', study_id, id],
    () => SpreadsheetService.checkSpreadsheet(whoAmI, study_id, id!),
    {
      enabled: !!whoAmI && !!study_id && !!id,
      onError: (err) => setError(err),
    },
  )

  window.onbeforeunload = () => {
    return window.location.pathname.includes('/stuart/study/') ?
        'ingored message but must be string'
      : null
  }

  const maxPolls = 30
  const secondInterval = 2
  const [pollsRemain, setPollsRemain] = useState(maxPolls)
  const [showHistoryToggle, setShowHistoryToggle] = useState<
    'show' | 'hide' | undefined
  >('hide')
  const [showDetailsToggle, setShowDetailsToggle] = useState<
    'show' | 'hide' | undefined
  >('show')

  useInterval(async () => {
    if (
      !error &&
      id &&
      !spreadsheet?.URL &&
      spreadsheet?.Status !== 'ERR' &&
      pollsRemain > 0
    ) {
      setPollsRemain((t) => t - 1)
      await refetch()
    }
  }, secondInterval * 1000) // to seconds

  const { mutate: showHistory } = useMutation<any, AxiosResponse>(
    () => SpreadsheetService.showSpreadsheetHistory(whoAmI, study_id, id!),
    {
      onError: (err) => setError(err),
    },
  )

  const { mutate: hideHistory } = useMutation<any, AxiosResponse>(
    () => SpreadsheetService.hideSpreadsheetHistory(whoAmI, study_id, id!),
    {
      onError: (err) => setError(err),
    },
  )

  const { mutate: showDetails } = useMutation<any, AxiosResponse>(
    () => SpreadsheetService.showSpreadsheetDetails(whoAmI, study_id, id!),
    {
      onError: (err) => setError(err),
    },
  )

  const { mutate: hideDetails } = useMutation<any, AxiosResponse>(
    () => SpreadsheetService.hideSpreadsheetDetails(whoAmI, study_id, id!),
    {
      onError: (err) => setError(err),
    },
  )

  const { mutate: abandonSheet, isLoading: abandonLoading } = useMutation<
    any,
    AxiosResponse
  >(() => SpreadsheetService.updateSpreadsheet(whoAmI, study_id, id!, true), {
    onError: (err) => setError(err),
    onSuccess: () => navigate('../../entry/select'),
  })

  if (location.pathname.endsWith('/sheet') && !id) {
    return (
      <Navigate
        to={'../../entry/select'}
        replace
      />
    )
  }
  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: 2,
      }}
    >
      <EntryStepsHeader step={1}>
        <Box sx={{ width: 1, display: 'flex' }}>
          <Box sx={{ display: 'flex', mr: 'auto' }}>
            <ButtonGroup>
              <Button
                onClick={() => {
                  showHistory()
                  setShowHistoryToggle('show')
                }}
                disabled={showHistoryToggle === 'show' || !spreadsheet?.URL}
                variant={'outlined'}
                size={'small'}
                startIcon={<Visibility />}
              >
                Show History
              </Button>
              <Button
                onClick={() => {
                  hideHistory()
                  setShowHistoryToggle('hide')
                }}
                disabled={showHistoryToggle === 'hide' || !spreadsheet?.URL}
                variant={'outlined'}
                size={'small'}
                startIcon={<VisibilityOff />}
              >
                Hide History
              </Button>
            </ButtonGroup>
            <ButtonGroup sx={{ ml: 1 }}>
              <Button
                onClick={() => {
                  showDetails()
                  setShowDetailsToggle('show')
                }}
                disabled={showDetailsToggle === 'show' || !spreadsheet?.URL}
                variant={'outlined'}
                size={'small'}
                startIcon={<Visibility />}
              >
                Show Details
              </Button>
              <Button
                onClick={() => {
                  hideDetails()
                  setShowDetailsToggle('hide')
                }}
                disabled={showDetailsToggle === 'hide' || !spreadsheet?.URL}
                variant={'outlined'}
                size={'small'}
                startIcon={<VisibilityOff />}
              >
                Hide Details
              </Button>
            </ButtonGroup>
          </Box>
          <Button
            onClick={() => setOpenFull(true)}
            color={'info'}
            variant={'outlined'}
            startIcon={<OpenInNew />}
            disabled={!spreadsheet?.URL || abandonLoading}
            sx={{ mr: 1 }}
          >
            Open Full Screen
          </Button>
          <Button
            onClick={() => abandonSheet()}
            color={'error'}
            variant={'contained'}
            startIcon={<Delete />}
            disabled={!spreadsheet?.URL || abandonLoading}
            sx={{ mr: 1 }}
          >
            Discard
          </Button>
          <Button
            onClick={() => {
              navigate('../complete?id=' + id)
            }}
            color={'success'}
            variant={'contained'}
            startIcon={<Save />}
            disabled={!spreadsheet?.URL}
          >
            Save & Close
          </Button>
        </Box>
        {spreadsheet && !spreadsheet?.URL && (
          <Box sx={{ mt: 2, width: 1, maxWidth: 'md' }}>
            {!error &&
              !spreadsheet?.URL &&
              spreadsheet?.Status !== 'ERR' &&
              pollsRemain > 0 && <LinearProgress sx={{ mb: 1, width: 1 }} />}
            {pollsRemain > 0 ?
              <Alert severity={spreadsheet.Status === 'ERR' ? 'error' : 'info'}>
                <Box
                  sx={{
                    minWidth: 160,
                    width: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Status:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{spreadsheet.Status}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Message:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{spreadsheet.StatusMessage}</Typography>
                  </Box>
                </Box>
              </Alert>
            : <Alert severity={'error'}>
                <Box
                  sx={{
                    minWidth: 160,
                    width: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Timeout:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{`The spreadsheet is taking too long to load. The UI has reached its polling limit of ${maxPolls * secondInterval}s`}</Typography>
                  </Box>
                </Box>
              </Alert>
            }
          </Box>
        )}
        {/*https://github.com/gatsbyjs/gatsby/issues/14872 issue requires a fix*/}
        {spreadsheet?.URL && (
          <Box sx={{ m: 1, width: 1 }}>
            <iframe
              title={'DataEntryGoogleSheet'}
              src={`https://docs.google.com/spreadsheets/d/${spreadsheet.URL}/edit#gid=0?rm=minimal`}
              width="100%"
              height="800"
            />
          </Box>
        )}
        <FullScreenModal
          open={openFull}
          setOpen={setOpenFull}
          url={spreadsheet?.URL}
        />
      </EntryStepsHeader>
    </Box>
  )
}

export default DataEntry
